import {
  TerminalEventMonitoring,
  TerminalModel,
} from '@lightspeed-payments/payments-terminal-sdk'
import { ApiRetailerPaymentType, ApiTransaction } from '@vend/types'
import React, { FC, ReactNode, useEffect, useMemo, useRef } from 'react'
import VendNumber from 'vend-number'
import { trackUserEvent } from '@vend/utilities'
import { useT } from '@transifex/react'
import { useVdFeatureManager } from '../../../../../../ng-compat/hooks'
import {
  convertDeviceTypeValueToKey,
  LSPAYPairing,
  Register,
  SwiperError,
  TerminalInfo,
} from '../../../helpers/lspay.types'
import {
  LSPAY_ERROR,
  SANDBOX_FLAG,
  TRANSACTION_TYPE,
} from '../../../helpers/lspayConstants'
import {
  getUnreferencedMessage,
  reprocessRefund,
} from '../../../helpers/refund'
import { getMonitoringInstance } from '../../../helpers/terminalMonitoring'
import { ErrorContent } from '../../modal-states/ErrorContent'
import { TAG, trackLSPayServerError } from '../../../hooks/errorTracking'
import { WEB_REGISTER } from '../../../../../web-register.constant'
import { sendTerminalEvent } from '../../../helpers/terminalMonitoringEventDispatcher'
import { CreatingRefund } from './components/CreatingRefund'
import { PollingRefund } from './components/PollingRefund'
import { useServerRefundReducer } from './hooks/use-lspay-refund-server-reducer/useServerRefundReducer'
import { useDispatchRefundEvent } from './hooks/useDispatchRefundEvent'
import { CardPresentPrompt } from './components/CardPresentPrompt'
import { OverRefundPrompt } from './components/OverRefundPrompt'
import { RefundRestrictionPrompt } from './components/RefundRestrictionPrompt'

interface LSPAYRefundProps {
  register: Register
  registerSaleId: string
  retailerPaymentType: ApiRetailerPaymentType
  originalRegisterSalePaymentIds?: string[]
  originalSalePaidByOtherPT?: boolean
  outletId: string
  amount: VendNumber
  disableUnrefRefund: boolean
  transactionId: string
  currency: string
  setDismissible: (dismissible: boolean) => void
  onSuccess: (transaction: Promise<ApiTransaction>) => void
  generateTransactionId: () => string
  onError: () => void
  pairing?: LSPAYPairing
}
export const LSPAYRefundServerDriven: FC<LSPAYRefundProps> = ({
  register,
  registerSaleId,
  retailerPaymentType,
  originalRegisterSalePaymentIds,
  originalSalePaidByOtherPT,
  outletId,
  amount,
  disableUnrefRefund,
  transactionId: registerSalePaymentId,
  currency,
  setDismissible,
  onSuccess,
  generateTransactionId,
  onError,
  pairing,
}) => {
  const [isRetrying, setIsRetrying] = React.useState(false)
  const [overrideAmount, setOverrideAmount] = React.useState<VendNumber>()
  const [refundState, dispatch] = useServerRefundReducer(
    originalRegisterSalePaymentIds,
    originalSalePaidByOtherPT,
    disableUnrefRefund
  )
  const isSandbox = useVdFeatureManager().get(SANDBOX_FLAG)
  const terminalMonitoringInstanceRef = useRef<TerminalEventMonitoring | null>(
    null
  )
  const t = useT()
  const { dispatchEvent, getErrorMessage } = useDispatchRefundEvent()

  if (!terminalMonitoringInstanceRef.current) {
    terminalMonitoringInstanceRef.current = getMonitoringInstance(
      outletId,
      isSandbox,
      'CLOUD'
    )
  }

  const terminalMonitoringInstance = terminalMonitoringInstanceRef.current
  const deviceType: TerminalModel = convertDeviceTypeValueToKey(
    pairing?.deviceType
  )

  const terminalInfo: TerminalInfo = useMemo(
    () => ({
      deviceId: pairing?.deviceID,
      deviceModel: deviceType,
    }),
    [pairing?.deviceID, deviceType]
  )

  useEffect(() => {
    if (refundState.status === 'done') {
      onSuccess(Promise.resolve(refundState.transaction))
    }
  }, [onSuccess, refundState])

  useEffect(() => {
    if (refundState.status === 'error' || refundState.status === 'cancelled') {
      setDismissible(true)
      return
    }
    setDismissible(false)
  }, [refundState, setDismissible])

  const handleContinueRefund = () => {
    // Send terminal monitoring event for starting the refund
    // here start actual card presented flow
    terminalMonitoringInstance?.sendEvent('START', {
      uniqueClientTxId: registerSalePaymentId,
      terminal: {
        id: terminalInfo?.deviceId,
        model: deviceType,
      },
      transactionType: TRANSACTION_TYPE.REFUND,
    })

    dispatch({
      type: 'CREATE_REFUND',
      consentCollected: true,
    })
  }

  const handleRetryRefund = () => {
    setIsRetrying(true)
    setDismissible(false)
    const setupIntentId =
      'setupIntentId' in refundState ? refundState.setupIntentId : ''
    // Track the event for retrying the refund
    trackUserEvent(
      WEB_REGISTER.DISPLAY_NAME,
      'LSD refund clicked retry button',
      {
        registerSalePaymentId,
        registerSaleId,
        setupIntentId,
      }
    )
    // Skip sending the terminal monitoring event if the error occurred during refund creation
    // and the refund is a non-interac referenced refund (without consent collected)
    // Send the event in all other cases of refund retries.

    const shouldSkipTerminalEvent =
      refundState.status === 'error' &&
      refundState.previousState.status === 'creatingRefund' &&
      !refundState.previousState.consentCollected

    if (!shouldSkipTerminalEvent) {
      // Send terminal monitoring event for retrying the refund
      terminalMonitoringInstance?.sendEvent('START', {
        uniqueClientTxId: registerSalePaymentId,
        terminal: {
          id: terminalInfo?.deviceId,
          model: terminalInfo?.deviceModel,
        },
        transactionType: TRANSACTION_TYPE.REFUND,
      })
    }

    // Special cases to start over from creating refund when the refund is not available to reprocess
    //   1. The refund has a reprocessing not available flag
    //   2. Retrying an errored refund during the creation step
    if (
      'reprocessingAvailable' in refundState &&
      !refundState.reprocessingAvailable
    ) {
      const oldRegisterSalePaymentId = registerSalePaymentId
      const newRegisterSalePaymentId = generateTransactionId()
      trackUserEvent(
        WEB_REGISTER.DISPLAY_NAME,
        'LSD user retried a cancelled refund with non-retryble reason which create a new refund transaction',
        {
          oldRegisterSalePaymentId,
          oldSetupIntentId: setupIntentId,
          newRegisterSalePaymentId,
          registerSaleId,
          reason: refundState.reason,
        }
      )

      dispatch({
        type: 'CREATE_REFUND',
        consentCollected: true,
      })
      setIsRetrying(false)
      return
    }

    if (
      refundState.status === 'error' &&
      refundState.previousState.status === 'creatingRefund'
    ) {
      const oldRegisterSalePaymentId = registerSalePaymentId
      const newRegisterSalePaymentId = generateTransactionId()
      trackUserEvent(
        WEB_REGISTER.DISPLAY_NAME,
        'LSD refund retry creating refund with new rspId when errored',
        {
          oldRegisterSalePaymentId,
          oldSetupIntentId: setupIntentId,
          newRegisterSalePaymentId,
          registerSaleId,
        }
      )

      dispatch({
        type: 'CREATE_REFUND',
        consentCollected: refundState.previousState.consentCollected,
        disableUnrefRefund: refundState.previousState.disableUnrefRefund,
      })
      setIsRetrying(false)
      return
    }
    reprocessRefund({
      setupIntentId,
      registerId: register.id,
      registerSalePaymentId,
    })
      .then(response => {
        // TODO: Double check the dispatchEvent function for the terminal monitoring events
        // To be matched with tm sendEvent similar to the payment

        dispatchEvent({
          dispatch,
          setupIntentId: response.setup_intent_id,
          terminalInfo,
          data: response,
          terminalMonitoringInstance,
        })
      })
      .catch((error: SwiperError) => {
        // track the error
        trackLSPayServerError(
          '[lspay-server-driven] failed to reprocess refund',
          error,
          {
            registerSalePaymentId,
            registerId: register.id,
            setupIntentId,
          },
          TAG.refund
        )
        // Send an error event to the terminal monitoring instance when the refund retry fails
        sendTerminalEvent({
          terminalMonitoringInstance,
          terminal: {
            id: terminalInfo?.deviceId,
            model: deviceType,
          },
          reason: error.message || `Error happened during retrying refund`,
          swiperError: error,
          transactionType: TRANSACTION_TYPE.REFUND,
        })

        dispatch({
          type: 'ERROR',
          errorMessage: getErrorMessage(LSPAY_ERROR.DEFAULT_REFUND),
          setupIntentId,
        })
      })
      .finally(() => {
        setIsRetrying(false)
      })
  }

  const getRefundContent = (): ReactNode => {
    switch (refundState.status) {
      case 'unreferencedPrompt': {
        return (
          <CardPresentPrompt
            close={onError}
            description={getUnreferencedMessage(
              refundState.unrefRefundReason,
              retailerPaymentType.name,
              t
            )}
            handleContinue={handleContinueRefund}
          />
        )
      }

      case 'creatingRefund': {
        return (
          <CreatingRefund
            amount={overrideAmount || amount}
            currency={currency}
            originalRegisterSalePaymentIds={originalRegisterSalePaymentIds}
            registerId={register.id}
            registerSaleId={registerSaleId}
            retailerPaymentTypeId={retailerPaymentType.id}
            registerSalePaymentId={registerSalePaymentId}
            consentCollected={refundState.consentCollected}
            dispatch={dispatch}
            terminalMonitoringInstance={terminalMonitoringInstance}
            terminalInfo={terminalInfo}
          />
        )
      }
      case 'interacCardPresentPrompt': {
        return (
          <CardPresentPrompt
            close={onError}
            handleContinue={handleContinueRefund}
          />
        )
      }

      case 'overRefundPrompt': {
        return (
          <OverRefundPrompt
            refundFullAmount={handleContinueRefund}
            refundOriginalAmount={() => {
              setOverrideAmount(refundState.refundableAmount)
              dispatch({
                type: 'CREATE_REFUND',
                consentCollected: false,
              })
            }}
            rptName={retailerPaymentType.name}
            refundableAmount={refundState.refundableAmount}
          />
        )
      }

      case 'refundRestrictionPrompt': {
        return (
          <RefundRestrictionPrompt
            close={onError}
            retailerPaymentTypeName={retailerPaymentType.name}
            refundableAmount={refundState.refundableAmount}
          />
        )
      }

      case 'pollingRefund': {
        return (
          <PollingRefund
            isCardPresented={refundState.isCardPresented}
            registerId={register.id}
            registerSalePaymentId={registerSalePaymentId}
            setupIntentId={refundState.setupIntentId}
            dispatch={dispatch}
            terminalMonitoringInstance={terminalMonitoringInstance}
            terminalInfo={terminalInfo}
          />
        )
      }

      case 'cancelled':
      case 'declined':
      case 'error': {
        return (
          <ErrorContent
            headingType="sub-heading"
            lspayError={refundState.errorMessage}
            disableRetry={refundState.disableRetry}
            retry={handleRetryRefund}
            showWarningIcon
            isRetrying={isRetrying}
            close={onError}
          />
        )
      }

      case 'done': {
        return
      }

      default: {
        // This `never` typing will let us know at compile time if we do not handle an existing event type. Neat !
        const _exhaustiveCheck: never = refundState
        return _exhaustiveCheck
      }
    }
  }

  return getRefundContent()
}
